import { CheckSquare, Copy, Lock, Maximize, Trash, Users, XSquare } from 'react-feather';
import { DocumentNode } from 'graphql';
import { Trans, useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  GetTicketNumberSequenceQuery as GetTicketNumberSequence,
  GetTicketNumberSequencesForEventQuery as GetTicketNumberSequencesForEvent,
  PurchaseStatus,
} from '__generated__/graphql';

import { encodeUrl } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import AssignTicketNumbersMutation from './AssignTicketNumbersMutation';
import DeleteTicketNumberSequenceMutation from './DeleteTicketNumberSequenceMutation';
import EditTicketNumberSequenceForm from './EditTicketNumberSequenceForm';
import GetTicketNumberSequenceQuery from './GetTicketNumberSequenceQuery';
import GetTicketNumberSequencesForEventQuery from './GetTicketNumberSequencesForEventQuery';
import OptionsDropdown, { Option } from '../../../common/Common/OptionsDropdown';
import RevokeTicketNumbersMutation from './RevokeTicketNumbersMutation';
import UI from '../../../common/UI';
import useConfirmModal from '../../../common/useConfirmModal';
import useDuplicateTicketNumberSequenceModal, {
  DuplicatedTicketNumberSequence,
} from './useDuplicateTicketNumberSequenceModal';
import useLazyQuery from '../../../api/useLazyQuery';
import useLocale from '../../../common/useLocale';
import useMediaDevice from '../../../common/useMediaDevice';
import useMutation from '../../../api/useMutation';
import useProject from '../../useProject';

type Event = GetTicketNumberSequencesForEvent['project']['event'];
type TicketNumberSequence = GetTicketNumberSequencesForEvent['project']['event']['ticket_number_sequences'][0];

interface TicketNumberSequencePreviewProps {
  sequence: TicketNumberSequence;
  potentialNumberCount?: number;
  event: Event;
  fetchPotentialNumberCount?: () => void;
  refetchQueries?: DocumentNode[];
}

const TicketNumberSequencePreview = ({
  sequence, potentialNumberCount, fetchPotentialNumberCount, event, refetchQueries,
}: TicketNumberSequencePreviewProps) => {
  const { t } = useTranslation();
  const { formatInteger, formatDate, parseDate } = useLocale();
  const project = useProject();
  const notifier = useNotifier();
  const history = useHistory();
  const device = useMediaDevice();

  const linkParams = {
    event: event.id,
    ticket_number_sequences: [sequence.id],
    statuses: [PurchaseStatus.complete, PurchaseStatus.incomplete],
  };

  const [assignTicketNumbers] = useMutation(
    AssignTicketNumbersMutation,
    {
      variables: {
        input: {
          event: { id: event.id },
          ticket_number_sequence: { id: sequence.id },
        },
      },
      optimisticResponse: {
        assignTicketNumbers: {
          id: event?.id,
          assigning_ticket_numbers: true,
          assigned_ticket_number_count: event?.assigned_ticket_number_count,
          missing_ticket_number_count: event?.missing_ticket_number_count,
          __typename: 'Event',
        },
      },
      onCompleted: () => {
        fetchPotentialNumberCount();
      },
    },
  );

  const [revokeTicketNumbers, { loading: revoking }] = useMutation(
    RevokeTicketNumbersMutation,
    {
      variables: {
        input: {
          event: { id: event.id },
          ticket_number_sequence: { id: sequence.id },
        },
      },
      onCompleted: () => {
        fetchPotentialNumberCount();
        notifier.success(t('notifications.ticket_numbers_reset'));
      },
      onError: () => notifier.error(t('notifications.something_went_wrong')),
    },
  );

  const assignableNumberCount = (sequence.capacity !== null
    ? Math.min(
      sequence.capacity - sequence.assigned_ticket_number_count,
      potentialNumberCount,
    )
    : potentialNumberCount) || 0;

  const [confirmAssign] = useConfirmModal({
    title: t('assign_ticket_numbers'),
    body: (
      <UI.Div>
        <Trans
          i18nKey="confirm_assign_ticket_numbers_for_sequence"
          values={{
            count: assignableNumberCount,
            value: formatInteger(assignableNumberCount),
            sequence: sequence.title,
          }}
        >
          <UI.Strong />
        </Trans>
        {sequence.assigned_ticket_number_count > 0 && (
          <UI.Info sc={{ mt: 2 }}>
            <Trans
              i18nKey="existing_ticket_numbers_are_kept_description"
              values={{
                count: sequence.assigned_ticket_number_count,
                value: formatInteger(sequence.assigned_ticket_number_count),
              }}
            >
              <UI.Strong />
            </Trans>
          </UI.Info>
        )}
      </UI.Div>
    ),
    onConfirm: () => {
      setAssigning(true);
      assignTicketNumbers();
    },
  }, [sequence, assignableNumberCount]);

  const [confirmRevoke] = useConfirmModal({
    title: t('revoke_ticket_numbers'),
    body: (
      <UI.Div>
        <Trans
          i18nKey="confirm_revoke_ticket_numbers_for_sequence"
          values={{
            count: sequence.assigned_ticket_number_count - sequence.locked_ticket_number_count,
            value: formatInteger(
              sequence.assigned_ticket_number_count - sequence.locked_ticket_number_count,
            ),
            sequence: sequence.title,
          }}
        >
          <UI.Strong />
        </Trans>
        {sequence.locked_ticket_number_count > 0 && (
          <UI.Info sc={{ mt: 2 }}>
            <Trans
              i18nKey="manual_ticket_numbers_are_kept_description"
              values={{
                count: sequence.locked_ticket_number_count,
                value: formatInteger(sequence.locked_ticket_number_count),
              }}
            >
              <UI.Strong />
            </Trans>
          </UI.Info>
        )}
      </UI.Div>
    ),
    onConfirm: () => revokeTicketNumbers(),
  }, [sequence]);

  const [assigning, setAssigning] = useState(false);

  const [openDuplicateSequenceModal] = useDuplicateTicketNumberSequenceModal({
    sequence,
    onSuccess: (sequence: DuplicatedTicketNumberSequence) => {
      history.push(`/${project.id}/participants/bib-numbers/${sequence.id}?${encodeUrl({ event: event.id })}`);
    },
  });

  const [deleteSequence, { loading: deleting }] = useMutation(
    DeleteTicketNumberSequenceMutation, {
      variables: {
        id: sequence.id,
      },
      onCompleted: () => {
        notifier.success(t('notifications.ticket_number_sequence_deleted'));

        history.push(`/${project.id}/participants/bib-numbers?${encodeUrl({ event: event.id })}`);
      },
      onError: () => {
        notifier.error(t('something_went_wrong'));
      },
      refetchQueries,
    },
  );

  const [confirmDelete] = useConfirmModal({
    title: t('delete_name', { name: sequence.title }),
    body: t('delete_ticket_number_sequence_description'),
    onConfirm: () => deleteSequence(),
  }, [sequence]);

  const onToggleDropdown = () => {
    if (typeof potentialNumberCount === 'undefined') {
      fetchPotentialNumberCount();
    }
  };

  useEffect(() => {
    if (!event.assigning_ticket_numbers) {
      setAssigning(false);
    }
  }, [event.assigning_ticket_numbers]);

  const assignable = !event.assigning_ticket_numbers && potentialNumberCount > 0
    && (sequence.capacity === null || sequence.assigned_ticket_number_count < sequence.capacity);

  const revokable = !event.assigning_ticket_numbers
    && sequence.assigned_ticket_number_count - sequence.locked_ticket_number_count > 0;

  return (
    <UI.GridContainer sc={{ alignVertical: 'center', columns: ['1fr 1fr 35px', '2fr 1fr 1fr 1fr 35px'], gutter: 0.5 }}>
      <UI.FlexContainer sc={{ spaceX: 2, alignItems: 'center' }} style={{ minWidth: 0 }}>
        <UI.H4 style={{ minWidth: 0 }}>
          <UI.Truncate>
            {sequence.title}
          </UI.Truncate>
        </UI.H4>
        {sequence.scanner && (
          <UI.TooltipContainer>
            <UI.Icon sc={{ muted: true }}>
              <Maximize />
            </UI.Icon>
            <UI.Tooltip sc={{ alignH: 'center', textAlign: 'center' }} style={{ width: 300, whiteSpace: 'normal' }}>
              {t('start_numbers_assigned_by_scanner')}
              {` (${sequence.scanner.title})`}
            </UI.Tooltip>
          </UI.TooltipContainer>
        )}
        {sequence.locked && (
          <UI.TooltipContainer>
            <UI.Icon sc={{ muted: true }}>
              <Lock />
            </UI.Icon>
            <UI.Tooltip>
              {t('registrations_locked')}
            </UI.Tooltip>
          </UI.TooltipContainer>
        )}
      </UI.FlexContainer>
      <UI.Div sc={{ color: 'gray.500' }}>
        {(sequence.corral_name || sequence.start_time) && (
          <>
            <UI.Icon>
              <UI.Icons.StartFinish />
            </UI.Icon>
            {' '}
            <UI.Delimit>
              {sequence.corral_name}
              {sequence.start_time && formatDate(
                parseDate(sequence.start_time, { format: 'internal_time' }),
                { format: 'display_time' },
              )}
            </UI.Delimit>
          </>
        )}
      </UI.Div>
      {!device.isSmall && (
        <UI.Div>
          <UI.Label sc={{ small: true }}>
            {sequence.example}
          </UI.Label>
        </UI.Div>
      )}
      {!device.isSmall && (
        <UI.Div>
          <UI.TooltipContainer sc={{ inlineBlock: true, padding: 1, margin: -1 }}>
            <UI.Link
              to={`/${project.id}/participants?${encodeUrl(linkParams)}`}
              sc={{ secondary: true }}
            >
              <UI.Icon>
                <Users />
              </UI.Icon>
              {` ${formatInteger(sequence.assigned_ticket_number_count)} / ${sequence.capacity !== null ? formatInteger(sequence.capacity) : '∞'}`}
            </UI.Link>
            <UI.Tooltip sc={{ alignH: 'center' }}>
              {t('participants')}
            </UI.Tooltip>
          </UI.TooltipContainer>
        </UI.Div>
      )}
      <UI.Div sc={{ textAlign: 'right' }} style={{ margin: '-4.5px 0' }}>
        {(assigning || revoking) && (
          <UI.Loader sc={{ brand: 'gray.400', mt: 1, mr: 2 }} />
        )}
        {!assigning && !revoking && (
          <OptionsDropdown onToggle={onToggleDropdown}>
            <Option
              icon={<CheckSquare />}
              label={t('assign')}
              onClick={() => confirmAssign()}
              disabled={!assignable}
            />
            {revokable && (
              <Option
                icon={<XSquare />}
                label={t('revoke')}
                onClick={() => confirmRevoke()}
              />
            )}
            <Option
              icon={<Copy />}
              label={t('duplicate')}
              onClick={() => openDuplicateSequenceModal()}
            />
            {sequence.deletable && (
              <Option
                icon={<Trash />}
                label={t('delete')}
                onClick={() => confirmDelete()}
                disabled={deleting}
              />
            )}
          </OptionsDropdown>
        )}
      </UI.Div>
    </UI.GridContainer>
  );
};

export interface TicketNumberSequenceCardProps {
  sequence: TicketNumberSequence;
  event: Event;
  active?: boolean;
  muted?: boolean;
  refetchQueries?: DocumentNode[];
}

const TicketNumberSequenceCard = ({
  sequence: preview, event, active = false, muted = false,
  refetchQueries = [GetTicketNumberSequencesForEventQuery],
}: TicketNumberSequenceCardProps) => {
  const project = useProject();

  const [fetch, { data }] = useLazyQuery<GetTicketNumberSequence>(
    GetTicketNumberSequenceQuery,
    {
      variables: {
        project_id: project.id,
        ticket_number_sequence_id: preview.id,
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  useEffect(() => {
    if (active) {
      fetch();
    }
  }, [active]);

  const sequence = data?.project.ticket_number_sequence;

  return (
    <UI.CollapsibleNavCard
      to={`/${project.id}/participants/bib-numbers/${!active ? preview.id : ''}?${encodeUrl({ event: event.id })}`}
      open={active}
      muted={muted}
      preview={(
        <TicketNumberSequencePreview
          sequence={preview}
          potentialNumberCount={sequence?.potential_ticket_number_count}
          fetchPotentialNumberCount={() => fetch()}
          event={event}
          refetchQueries={refetchQueries}
        />
      )}
      body={
        sequence
          ? <EditTicketNumberSequenceForm sequence={sequence} />
          : <UI.PageLoader />
      }
      id={`TicketNumberSequenceCard_${preview.id}`}
    />
  );
};

export default TicketNumberSequenceCard;
